import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  AlertTriangle, Clock, CheckCircle, Search, Eye, MessageSquare,
  ChevronDown, ChevronRight, User, Calendar, MapPin, FileText,
  AlertCircle, Shield, X, Send, Filter, RefreshCw, Plus, Printer,
  Archive, Edit, Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { incidentsAPI, bookingsAPI } from '../../services/api';

// Sanitize HTML helper
const sanitizeHtml = (html) => DOMPurify.sanitize(html);

const INCIDENT_TYPES = {
  worker_no_show: { label: 'Worker No-Show', color: 'bg-[var(--error)]/15 text-red-700' },
  worker_unprofessional: { label: 'Unprofessional Behavior', color: 'bg-orange-100 text-orange-700' },
  worker_safety: { label: 'Safety Issue', color: 'bg-[var(--error)]/15 text-red-700' },
  customer_harassment: { label: 'Customer Harassment', color: 'bg-[#F4C2C2]/15 text-purple-700' },
  customer_unsafe_environment: { label: 'Unsafe Environment', color: 'bg-[var(--error)]/15 text-red-700' },
  customer_payment_dispute: { label: 'Payment Dispute', color: 'bg-amber-100 text-amber-700' },
  service_property_damage: { label: 'Property Damage', color: 'bg-[var(--error)]/15 text-red-700' },
  service_incomplete_work: { label: 'Incomplete Work', color: 'bg-[var(--warning)]/15 text-yellow-700' },
  service_quality_issue: { label: 'Quality Issue', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  other: { label: 'Other', color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]' },
};

const STATUS_CONFIG = {
  received: { label: 'Received', color: 'bg-[#D4AF37]/10 text-[#D4AF37]', icon: AlertCircle },
  investigating: { label: 'Investigating', color: 'bg-amber-100 text-amber-700', icon: Search },
  actions_taken: { label: 'Actions Taken', color: 'bg-[#F4C2C2]/15 text-purple-700', icon: Shield },
  outcome_findings: { label: 'Outcome/Findings', color: 'bg-indigo-100 text-indigo-700', icon: FileText },
  solved: { label: 'Solved', color: 'bg-[var(--success)]/15 text-green-700', icon: CheckCircle },
  archived: { label: 'Archived', color: 'bg-[var(--background-subtle)] text-[var(--text-muted)]', icon: Archive },
};

const SEVERITY_CONFIG = {
  low: { label: 'Low', color: 'bg-[var(--background-subtle)] text-[var(--text-secondary)]' },
  medium: { label: 'Medium', color: 'bg-[var(--warning)]/15 text-yellow-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critical', color: 'bg-[var(--error)]/15 text-red-700' },
};

const CATEGORY_CONFIG = {
  worker_related: { label: 'Worker Related', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  customer_related: { label: 'Customer Related', color: 'bg-[var(--success)]/10 text-green-700' },
  service_related: { label: 'Service Related', color: 'bg-[#F4C2C2]/10 text-purple-700' },
};

const IncidentsTab = () => {
  const [incidents, setIncidents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  
  // Status update form
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Add note form
  const [noteText, setNoteText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [addingNote, setAddingNote] = useState(false);

  // Create/Edit form
  const [incidentForm, setIncidentForm] = useState({
    booking_id: '',
    incident_type: '',
    category: 'service_related',
    title: '',
    description: '',
    severity: 'medium'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
    fetchBookings();
  }, [statusFilter, categoryFilter, severityFilter, showArchived]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (severityFilter !== 'all') params.severity = severityFilter;
      
      const [incidentsRes, statsRes] = await Promise.all([
        incidentsAPI.getAll(params),
        incidentsAPI.getStats()
      ]);
      
      let incidentsList = incidentsRes.data.incidents || [];
      
      // Filter archived
      if (!showArchived) {
        incidentsList = incidentsList.filter(i => i.status !== 'archived');
      }
      
      setIncidents(incidentsList);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching incidents:', error);
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await bookingsAPI.getAll();
      setBookings(res.data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const handleCreateIncident = async () => {
    if (!incidentForm.booking_id || !incidentForm.incident_type || !incidentForm.title || !incidentForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (incidentForm.description.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    
    setSubmitting(true);
    try {
      await incidentsAPI.create(incidentForm);
      toast.success('Incident report created');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create incident');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateIncident = async () => {
    if (!incidentForm.title || !incidentForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      // Update via status endpoint with edit flag
      await incidentsAPI.updateStatus(selectedIncident.id, {
        status: selectedIncident.status,
        status_notes: `Incident edited: Title and description updated`,
        internal_notes: `Previous title: ${selectedIncident.title}`
      });
      toast.success('Incident updated');
      setShowEditModal(false);
      resetForm();
      fetchData();
      // Refresh selected
      const res = await incidentsAPI.get(selectedIncident.id);
      setSelectedIncident(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update incident');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchiveIncident = async (incident) => {
    if (!window.confirm('Are you sure you want to archive this incident?')) return;
    
    try {
      await incidentsAPI.updateStatus(incident.id, {
        status: 'archived',
        status_notes: 'Incident archived by owner'
      });
      toast.success('Incident archived');
      fetchData();
      if (selectedIncident?.id === incident.id) {
        setSelectedIncident(null);
      }
    } catch (error) {
      toast.error('Failed to archive incident');
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus || !statusNotes.trim()) {
      toast.error('Please select a status and provide notes');
      return;
    }
    
    setUpdatingStatus(true);
    try {
      await incidentsAPI.updateStatus(selectedIncident.id, {
        status: newStatus,
        status_notes: statusNotes,
        internal_notes: internalNotes || null
      });
      toast.success('Incident status updated');
      setShowStatusModal(false);
      setNewStatus('');
      setStatusNotes('');
      setInternalNotes('');
      fetchData();
      const res = await incidentsAPI.get(selectedIncident.id);
      setSelectedIncident(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    setAddingNote(true);
    try {
      await incidentsAPI.addNote(selectedIncident.id, {
        note: noteText,
        is_internal: isInternalNote
      });
      toast.success('Note added');
      setShowNoteModal(false);
      setNoteText('');
      setIsInternalNote(false);
      const res = await incidentsAPI.get(selectedIncident.id);
      setSelectedIncident(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handlePrintIncident = (incident) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Incident Report - ${incident.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          h2 { color: #2d3748; margin-top: 30px; }
          .header-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; }
          .status-received { background: #dbeafe; color: #1e40af; }
          .status-investigating { background: #fef3c7; color: #b45309; }
          .status-actions_taken { background: #e9d5ff; color: #7c3aed; }
          .status-outcome_findings { background: #c7d2fe; color: #4338ca; }
          .status-solved { background: #d1fae5; color: #059669; }
          .severity-low { background: #f3f4f6; color: #6b7280; }
          .severity-medium { background: #fef9c3; color: #ca8a04; }
          .severity-high { background: #fed7aa; color: #ea580c; }
          .severity-critical { background: #fee2e2; color: #dc2626; }
          .section { background: #f9fafb; padding: 15px; border-radius: 8px; margin: 15px 0; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .info-item { margin: 5px 0; }
          .info-label { font-weight: bold; color: #6b7280; font-size: 12px; }
          .timeline { border-left: 2px solid #e5e7eb; padding-left: 20px; margin-left: 10px; }
          .timeline-item { margin: 15px 0; position: relative; }
          .timeline-item::before { content: ''; width: 10px; height: 10px; background: #3b82f6; border-radius: 50%; position: absolute; left: -26px; top: 5px; }
          .timeline-status { font-weight: bold; }
          .timeline-notes { color: #4b5563; margin-top: 5px; }
          .timeline-meta { font-size: 11px; color: #9ca3af; margin-top: 5px; }
          .internal-note { background: #fef3c7; border-left: 3px solid #f59e0b; padding: 10px; margin: 5px 0; }
          .print-footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Incident Report</h1>
        
        <div class="header-info">
          <div>
            <span class="badge status-${incident.status}">${STATUS_CONFIG[incident.status]?.label || incident.status}</span>
            <span class="badge severity-${incident.severity}">${SEVERITY_CONFIG[incident.severity]?.label || incident.severity}</span>
          </div>
          <div>
            <strong>ID:</strong> ${incident.id.slice(-8).toUpperCase()}
          </div>
        </div>
        
        <h2>${incident.title}</h2>
        
        <div class="section">
          <div class="info-label">DESCRIPTION</div>
          <p>${incident.description}</p>
        </div>
        
        <div class="info-grid">
          <div class="section">
            <div class="info-label">INCIDENT DETAILS</div>
            <div class="info-item"><strong>Type:</strong> ${INCIDENT_TYPES[incident.incident_type]?.label || incident.incident_type}</div>
            <div class="info-item"><strong>Category:</strong> ${CATEGORY_CONFIG[incident.category]?.label || incident.category}</div>
            <div class="info-item"><strong>Reported:</strong> ${new Date(incident.created_at).toLocaleString()}</div>
            <div class="info-item"><strong>Reporter:</strong> ${incident.reporter_name} (${incident.reporter_role})</div>
          </div>
          
          ${incident.booking_info ? `
          <div class="section">
            <div class="info-label">RELATED BOOKING</div>
            <div class="info-item"><strong>Service:</strong> ${incident.booking_info.service_name}</div>
            <div class="info-item"><strong>Date:</strong> ${incident.booking_info.scheduled_date} at ${incident.booking_info.scheduled_time}</div>
            <div class="info-item"><strong>Customer:</strong> ${incident.booking_info.customer_name}</div>
            <div class="info-item"><strong>Worker:</strong> ${incident.booking_info.worker_name || 'Not assigned'}</div>
          </div>
          ` : ''}
        </div>
        
        <h2>Status History</h2>
        <div class="timeline">
          ${(incident.status_history || []).map(entry => `
            <div class="timeline-item">
              <div class="timeline-status">${STATUS_CONFIG[entry.status]?.label || entry.status}</div>
              <div class="timeline-notes">${entry.notes}</div>
              ${entry.internal_notes ? `<div class="internal-note"><strong>Internal:</strong> ${entry.internal_notes}</div>` : ''}
              <div class="timeline-meta">${entry.updated_by_name} • ${new Date(entry.updated_at).toLocaleString()}</div>
            </div>
          `).join('')}
        </div>
        
        ${incident.notes?.length > 0 ? `
        <h2>Notes</h2>
        ${incident.notes.map(note => `
          <div class="section ${note.is_internal ? 'internal-note' : ''}">
            ${note.is_internal ? '<div class="info-label">INTERNAL NOTE</div>' : ''}
            <p>${note.note}</p>
            <div class="timeline-meta">${note.added_by_name} • ${new Date(note.added_at).toLocaleString()}</div>
          </div>
        `).join('')}
        ` : ''}
        
        <div class="print-footer">
          <p>Printed on ${new Date().toLocaleString()} | NC Help Hub Incident Management System</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      const doc = printWindow.document;
      const html = doc.createElement('html');
      const head = doc.createElement('head');
      const body = doc.createElement('body');
      const container = doc.createElement('div');
      container.innerHTML = sanitizeHtml(printContent);
      body.appendChild(container);
      html.appendChild(head);
      html.appendChild(body);
      doc.documentElement.replaceWith(html);
      setTimeout(() => { printWindow.focus(); printWindow.print(); }, 300);
    }
  };

  const resetForm = () => {
    setIncidentForm({
      booking_id: '',
      incident_type: '',
      category: 'service_related',
      title: '',
      description: '',
      severity: 'medium'
    });
  };

  const openEditModal = (incident) => {
    setIncidentForm({
      booking_id: incident.booking_id,
      incident_type: incident.incident_type,
      category: incident.category,
      title: incident.title,
      description: incident.description,
      severity: incident.severity
    });
    setShowEditModal(true);
  };

  const filteredIncidents = incidents.filter(incident => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        incident.title?.toLowerCase().includes(query) ||
        incident.description?.toLowerCase().includes(query) ||
        incident.reporter_name?.toLowerCase().includes(query) ||
        incident.booking_info?.customer_name?.toLowerCase().includes(query) ||
        incident.booking_info?.worker_name?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Incident Reports</h1>
          <p className="text-[var(--text-muted)] mt-1">Track and manage reported incidents</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" data-testid="refresh-incidents-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="btn-primary" data-testid="create-incident-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Incident
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--error)]">{stats.pending_count || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Pending</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{stats.by_status?.received || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">New</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.by_status?.investigating || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Investigating</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#F4C2C2]">{(stats.by_status?.actions_taken || 0) + (stats.by_status?.outcome_findings || 0)}</p>
            <p className="text-sm text-[var(--text-muted)]">In Progress</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{stats.by_status?.solved || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Solved</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search incidents..."
                className="pl-10 input-styled"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'archived').map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-4 h-4"
            />
            Show Archived
          </label>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Incidents List */}
        <div className="space-y-4">
          <h2 className="font-semibold text-lg">
            Incidents ({filteredIncidents.length})
          </h2>
          
          {filteredIncidents.length === 0 ? (
            <div className="card p-12 text-center">
              <AlertTriangle className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4" />
              <h3 className="font-semibold mb-2">No Incidents Found</h3>
              <p className="text-[var(--text-muted)]">No incidents match your current filters</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {filteredIncidents.map(incident => {
                const statusConfig = STATUS_CONFIG[incident.status] || STATUS_CONFIG.received;
                const severityConfig = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.medium;
                const typeConfig = INCIDENT_TYPES[incident.incident_type] || INCIDENT_TYPES.other;
                const isSelected = selectedIncident?.id === incident.id;
                
                return (
                  <div
                    key={incident.id}
                    onClick={() => setSelectedIncident(incident)}
                    className={`card p-4 cursor-pointer transition-all ${
                      isSelected ? 'ring-2 ring-[var(--primary)] bg-[var(--accent)]' : 'hover:shadow-md'
                    } ${incident.status === 'archived' ? 'opacity-60' : ''}`}
                    data-testid={`incident-${incident.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-1 rounded-full ${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${severityConfig.color}`}>
                          {severityConfig.label}
                        </span>
                      </div>
                      <span className="text-xs text-[var(--text-muted)]">
                        {new Date(incident.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <h3 className="font-semibold mb-1">{incident.title}</h3>
                    <p className="text-sm text-[var(--text-muted)] line-clamp-2 mb-2">
                      {incident.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-[var(--text-muted)]">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {incident.reporter_name}
                        </span>
                        <span className={`px-2 py-0.5 rounded ${typeConfig.color}`}>
                          {typeConfig.label}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handlePrintIncident(incident); }}
                          title="Print"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Incident Details */}
        <div>
          {selectedIncident ? (
            <div className="card p-6 sticky top-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-sm ${STATUS_CONFIG[selectedIncident.status]?.color}`}>
                      {STATUS_CONFIG[selectedIncident.status]?.label}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-sm ${SEVERITY_CONFIG[selectedIncident.severity]?.color}`}>
                      {SEVERITY_CONFIG[selectedIncident.severity]?.label}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold">{selectedIncident.title}</h2>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handlePrintIncident(selectedIncident)} title="Print">
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(selectedIncident)} title="Edit">
                    <Edit className="w-4 h-4" />
                  </Button>
                  {selectedIncident.status !== 'archived' && (
                    <Button variant="ghost" size="sm" onClick={() => handleArchiveIncident(selectedIncident)} title="Archive">
                      <Archive className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => setSelectedIncident(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Incident Info */}
              <div className="space-y-4 mb-6">
                <div className="p-3 bg-[var(--background-subtle)] rounded-lg">
                  <p className="text-sm font-medium mb-1">Description</p>
                  <p className="text-sm text-[var(--text-secondary)]">{selectedIncident.description}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Reporter</p>
                    <p className="font-medium">{selectedIncident.reporter_name}</p>
                    <p className="text-xs text-[var(--text-muted)] capitalize">{selectedIncident.reporter_role}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-muted)]">Incident Type</p>
                    <p className="font-medium">{INCIDENT_TYPES[selectedIncident.incident_type]?.label}</p>
                    <p className="text-xs text-[var(--text-muted)] capitalize">{selectedIncident.category?.replace('_', ' ')}</p>
                  </div>
                </div>

                {selectedIncident.booking_info && (
                  <div className="p-3 bg-[#D4AF37]/10 rounded-lg">
                    <p className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Related Booking
                    </p>
                    <div className="text-sm space-y-1">
                      <p><strong>Service:</strong> {selectedIncident.booking_info.service_name}</p>
                      <p><strong>Date:</strong> {selectedIncident.booking_info.scheduled_date} at {selectedIncident.booking_info.scheduled_time}</p>
                      <p><strong>Customer:</strong> {selectedIncident.booking_info.customer_name}</p>
                      <p><strong>Worker:</strong> {selectedIncident.booking_info.worker_name || 'Not assigned'}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              {selectedIncident.status !== 'archived' && (
                <div className="flex gap-2 mb-6">
                  <Button
                    onClick={() => {
                      setNewStatus(selectedIncident.status);
                      setShowStatusModal(true);
                    }}
                    className="btn-primary flex-1"
                    data-testid="update-status-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Update Status
                  </Button>
                  <Button
                    onClick={() => setShowNoteModal(true)}
                    variant="outline"
                    className="flex-1"
                    data-testid="add-note-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                </div>
              )}

              {/* Status History */}
              <div className="mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Status History
                </h3>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {selectedIncident.status_history?.map((entry, idx) => (
                    <div key={idx} className="flex gap-3 text-sm">
                      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-[var(--primary)] mt-2" />
                      <div>
                        <p className="font-medium">
                          {STATUS_CONFIG[entry.status]?.label || entry.status}
                        </p>
                        <p className="text-[var(--text-muted)]">{entry.notes}</p>
                        {entry.internal_notes && (
                          <p className="text-xs text-amber-600 mt-1 p-2 bg-[var(--warning)]/10 rounded">
                            Internal: {entry.internal_notes}
                          </p>
                        )}
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                          {entry.updated_by_name} • {new Date(entry.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {selectedIncident.notes?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3">Notes</h3>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {selectedIncident.notes.map((note) => (
                      <div
                        key={note.added_at}
                        className={`p-3 rounded-lg text-sm ${note.is_internal ? 'bg-[var(--warning)]/10 border border-[var(--warning)]/30' : 'bg-[var(--background-subtle)]'}`}
                      >
                        {note.is_internal && (
                          <span className="text-xs font-medium text-amber-600 block mb-1">Internal Note</span>
                        )}
                        <p>{note.note}</p>
                        <p className="text-xs text-[var(--text-muted)] mt-1">
                          {note.added_by_name} • {new Date(note.added_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card p-12 text-center">
              <Eye className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Select an Incident</h3>
              <p className="text-[var(--text-muted)]">Click on an incident to view details and take action</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Incident Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b sticky top-0 bg-[var(--background-paper)]">
              <h2 className="text-xl font-bold">Create Incident Report</h2>
              <p className="text-sm text-[var(--text-muted)]">Manually create an incident report</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Select Booking *</Label>
                <Select value={incidentForm.booking_id} onValueChange={(v) => setIncidentForm({...incidentForm, booking_id: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Choose booking" />
                  </SelectTrigger>
                  <SelectContent>
                    {bookings.filter(b => ['confirmed', 'completed', 'in_progress', 'assigned'].includes(b.status)).map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.service_name} - {b.scheduled_date} ({b.customer_name || 'Unknown'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select value={incidentForm.category} onValueChange={(v) => setIncidentForm({...incidentForm, category: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Incident Type *</Label>
                  <Select value={incidentForm.incident_type} onValueChange={(v) => setIncidentForm({...incidentForm, incident_type: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(INCIDENT_TYPES).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Severity</Label>
                <Select value={incidentForm.severity} onValueChange={(v) => setIncidentForm({...incidentForm, severity: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Title *</Label>
                <Input
                  value={incidentForm.title}
                  onChange={(e) => setIncidentForm({...incidentForm, title: e.target.value})}
                  placeholder="Brief summary of the incident"
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Description * (min 20 characters)</Label>
                <Textarea
                  value={incidentForm.description}
                  onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
                  placeholder="Describe what happened in detail..."
                  className="mt-1 min-h-[100px]"
                />
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3 justify-end sticky bottom-0 bg-[var(--background-paper)]">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreateIncident}
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? 'Creating...' : 'Create Incident'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Incident Modal */}
      {showEditModal && selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg"
          >
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Edit Incident Report</h2>
              <p className="text-sm text-[var(--text-muted)]">Update incident details</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={incidentForm.title}
                  onChange={(e) => setIncidentForm({...incidentForm, title: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={incidentForm.description}
                  onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
                  className="mt-1 min-h-[100px]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={incidentForm.category} onValueChange={(v) => setIncidentForm({...incidentForm, category: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity</Label>
                  <Select value={incidentForm.severity} onValueChange={(v) => setIncidentForm({...incidentForm, severity: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SEVERITY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateIncident}
                disabled={submitting}
                className="btn-primary"
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Update Status Modal */}
      {showStatusModal && selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg"
          >
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Update Incident Status</h2>
              <p className="text-sm text-[var(--text-muted)]">{selectedIncident.title}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>New Status *</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).filter(([k]) => k !== 'archived').map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <config.icon className="w-4 h-4" />
                          {config.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Status Notes * (visible to reporter)</Label>
                <Textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  placeholder="Explain the status change..."
                  className="mt-1 min-h-[80px]"
                />
              </div>
              
              <div>
                <Label>Internal Notes (owner only)</Label>
                <Textarea
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Private notes for your records..."
                  className="mt-1 min-h-[60px]"
                />
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowStatusModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdateStatus}
                disabled={updatingStatus || !newStatus || !statusNotes.trim()}
                className="btn-primary"
              >
                {updatingStatus ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && selectedIncident && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg"
          >
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">Add Note</h2>
              <p className="text-sm text-[var(--text-muted)]">{selectedIncident.title}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Note *</Label>
                <Textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Enter your note..."
                  className="mt-1 min-h-[100px]"
                />
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-[var(--warning)]/10 rounded-lg">
                <input
                  type="checkbox"
                  id="isInternal"
                  checked={isInternalNote}
                  onChange={(e) => setIsInternalNote(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="isInternal" className="text-sm">
                  <span className="font-medium">Internal Note</span>
                  <span className="text-[var(--text-muted)] block">Only visible to you (owner)</span>
                </label>
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowNoteModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddNote}
                disabled={addingNote || !noteText.trim()}
                className="btn-primary"
              >
                {addingNote ? 'Adding...' : 'Add Note'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default IncidentsTab;
